import { createRoot, type Root } from "react-dom/client";
import { createElement } from "react";
import { ensureStyle, DOCUMENT_CSS } from "@broadpaper/renderer";
import type { ReportTemplate } from "@broadpaper/core";
import { EditorSession } from "./state/session.js";
import { ReportDesignerRoot } from "./ReportDesigner.js";
import { createActions } from "./state/actions.js";
import { EDITOR_CSS } from "./styles/generated-css.js";
import type { ReportDesignerApi, ReportDesignerOptions } from "./options.js";

export interface ReportDesignerInstance extends ReportDesignerApi {
  /** The React root; exposed for advanced hosts (testing, devtools). */
  readonly element: HTMLElement;
}

/**
 * Mounts the report designer into an element. Framework-neutral: the React
 * and Angular packages are thin wrappers around this function.
 *
 *   const designer = createReportDesigner(el, { dataSources, theme, onChange });
 *   designer.getTemplate();
 *   designer.destroy();
 */
export function createReportDesigner(element: HTMLElement, options: ReportDesignerOptions = {}): ReportDesignerInstance {
  const doc = element.ownerDocument;
  ensureStyle(doc, "bpe-styles", EDITOR_CSS);
  ensureStyle(doc, "bp-document-css", DOCUMENT_CSS);
  const session = new EditorSession(doc, options);
  const actions = createActions(session);
  // Mount into our own child so repeated mount/unmount on the same host (React strict mode) never reuses a root container.
  const mountEl = doc.createElement("div");
  mountEl.style.cssText = "width:100%;height:100%;";
  element.appendChild(mountEl);
  let root: Root | null = createRoot(mountEl);
  root.render(createElement(ReportDesignerRoot, { session }));

  const api: ReportDesignerInstance = {
    element,
    getTemplate: () => session.store.template,
    setTemplate: (t) => session.setTemplate(t),
    getStore: () => session.store,
    setTheme: (theme) => {
      const themes = session.options.get().themes ?? [];
      session.options.set({ theme, themes: themes.some((t) => t.id === theme.id) ? themes : [theme, ...themes] });
      session.setTheme(theme.id);
    },
    setThemes: (themes) => {
      session.options.set({ themes });
      if (!themes.some((t) => t.id === session.ui.get().activeThemeId) && themes[0]) session.setTheme(themes[0].id);
    },
    setSampleData: (data) => {
      session.options.set({ sampleData: data });
      const sets = session.sampleSets;
      if (!sets.some((s) => s.id === session.ui.get().activeSampleId)) session.ui.patch({ activeSampleId: sets[0]?.id ?? null });
      session.requestLayout();
    },
    setDataSources: (dataSources) => {
      session.options.set({ dataSources });
      session.scheduleValidation();
    },
    registerBlock: (def) => session.registerBlock(def),
    validate: () => session.validateNow(),
    undo: () => actions.undo(),
    redo: () => actions.redo(),
    setMode: (mode) => session.setMode(mode),
    getMode: () => session.store.getState().mode,
    select: (ids) => session.store.select(ids),
    exportPdf: () => actions.exportPdf(),
    print: () => actions.print(),
    getLayout: () => session.layout.get(),
    update: (patch) => {
      session.options.set(patch);
      if (patch.template) session.setTemplate(patch.template as ReportTemplate);
      if (patch.theme) api.setTheme(patch.theme);
      if (patch.blocks) for (const b of patch.blocks) session.registerBlock(b);
      session.scheduleValidation();
    },
    destroy: () => {
      const r = root;
      root = null;
      // Defer: hosts often destroy from a React effect cleanup, where a synchronous unmount is illegal.
      setTimeout(() => {
        r?.unmount();
        mountEl.remove();
      }, 0);
      session.destroy();
    }
  };
  session.scheduleValidation();
  queueMicrotask(() => options.onReady?.(api));
  return api;
}
